<template>
  <div class="space-y-6">
    <!-- Header Section -->
    <div class="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 text-white">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold">Gestión de Usuarios</h1>
          <p class="text-blue-100 mt-1">Administra los usuarios del sistema</p>
        </div>
        <div class="hidden sm:block">
          <div class="bg-white/20 rounded-lg p-4">
            <svg class="h-12 w-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="card p-6">
      <div class="flex flex-col lg:flex-row gap-4 items-end">
        <!-- Search Filter -->
        <div class="w-full lg:flex-1">
          <label class="block text-sm font-medium text-gray-700 mb-2">Buscar</label>
          <input
            v-model="globalFilter"
            type="text"
            placeholder="Buscar por nombre, email o rol..."
            class="block w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
          />
        </div>



        <!-- Plan Filter -->
        <div class="w-full lg:w-48">
          <label class="block text-sm font-medium text-gray-700 mb-2">Plan</label>
          <select
            v-model="planFilter"
            class="block w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
          >
            <option value="">Todos los planes</option>
            <option value="free">Free</option>
            <option value="premium">Premium</option>
          </select>
        </div>

        <!-- Add Button -->
        <div class="flex-shrink-0">
          <button
            @click="router.push('/users/create')"
            class="inline-flex items-center px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200 shadow-lg"
          >
            <svg class="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Crear Usuario
          </button>
        </div>
      </div>
    </div>

    <!-- DataTable with PrimeVue -->
    <div class="card">
      <DataTable
        :value="filteredUsers"
        :loading="loading"
        paginator
        :rows="10"
        :rowsPerPageOptions="[5, 10, 20, 50]"
        tableStyle="min-width: 50rem"
        class="p-datatable-sm"
      >
        <template #header>
          <div class="flex justify-between items-center">
            <h2 class="text-lg font-semibold text-gray-900">Lista de Usuarios</h2>
          </div>
        </template>
        
        <template #empty>
          <div class="text-center py-12">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">No hay usuarios</h3>
            <p class="mt-1 text-sm text-gray-500">Comienza agregando un nuevo usuario.</p>
          </div>
        </template>
        
        <template #loading>
          <div class="text-center py-8">
            <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p class="mt-2 text-gray-600">Cargando usuarios...</p>
          </div>
        </template>

        <Column field="name" header="Usuario" sortable>
          <template #body="{ data }">
            <div class="flex items-center">
              <div class="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold mr-3">
                {{ data.name?.charAt(0).toUpperCase() }}
              </div>
              <div>
                <router-link 
                  :to="`/user/${data.id}`" 
                  class="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200"
                >
                  {{ data.name }}
                </router-link>
                <div class="text-sm text-gray-500">{{ data.email }}</div>
              </div>
            </div>
          </template>
        </Column>

        <Column field="role" header="Rol" sortable>
          <template #body="{ data }">
            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" :class="{
              'bg-purple-100 text-purple-800': data.role === 'admin',
              'bg-blue-100 text-blue-800': data.role === 'user'
            }">
              {{ data.role === 'admin' ? 'Administrador' : 'Usuario' }}
            </span>
          </template>
        </Column>

        <Column field="plan" header="Plan">
          <template #body="{ data }">
            <span
              class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
              :style="data.plan?.type === 'premium' ? 'background:#fef3c7;color:#92400e' : 'background:#f3f4f6;color:#374151'"
            >
              {{ data.plan?.type === 'premium' ? 'Premium' : 'Free' }}
            </span>
          </template>
        </Column>

        <Column field="isBlocked" header="Estado">
          <template #body="{ data }">
            <span
              class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
              :class="data.isBlocked ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'"
            >
              {{ data.isBlocked ? 'Bloqueado' : 'Activo' }}
            </span>
          </template>
        </Column>

        <Column field="createdAt" header="Fecha de Registro" sortable>
          <template #body="{ data }">
            <span class="text-sm text-gray-900">
              {{ new Date(data.createdAt).toLocaleDateString('es-ES') }}
            </span>
          </template>
        </Column>

        <Column header="Acciones">
          <template #body="{ data }">
            <div class="flex items-center space-x-2">
              <button
                @click="$router.push(`/user/${data.id}/edit`)"
                style="background-color:#1d4ed8;color:#fff"
                class="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-lg transition-opacity hover:opacity-90"
                title="Editar usuario"
              >
                <svg class="h-3.5 w-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Editar
              </button>
              <button
                v-if="!data.isBlocked"
                @click="openBlockModal(data)"
                class="inline-flex items-center p-2 text-sm font-medium text-orange-600 bg-orange-50 rounded-lg hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all duration-200"
                title="Bloquear usuario"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </button>
              <button
                v-else
                @click="() => { userToUnblock = data; showUnblockModal = true }"
                :disabled="blockLoading"
                class="inline-flex items-center p-2 text-sm font-medium text-green-600 bg-green-50 rounded-lg hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-200"
                title="Desbloquear usuario"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                </svg>
              </button>
              <button
                @click="confirmDelete(data)"
                class="inline-flex items-center p-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all duration-200"
                title="Eliminar usuario"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </template>
        </Column>
      </DataTable>
      

    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" @click="closeModal"></div>

        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div class="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full border border-gray-100">
          <form @submit.prevent="saveUser">
            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div class="sm:flex sm:items-start">
                <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    {{ isEditing ? 'Editar Usuario' : 'Agregar Nuevo Usuario' }}
                  </h3>
                  <div class="mt-6 space-y-6">
                    <!-- Name -->
                    <div class="group">
                      <label for="name" class="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                        <svg class="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Nombre Completo *
                      </label>
                      <div class="relative">
                        <input
                          v-model="userForm.name"
                          type="text"
                          id="name"
                          required
                          class="w-full px-4 py-3 border-2 border-gray-200 rounded-xl shadow-sm transition-all duration-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 focus:outline-none hover:border-gray-300 bg-gray-50 focus:bg-white"
                          placeholder="Ingresa el nombre completo"
                        />
                      </div>
                    </div>

                    <!-- Email -->
                    <div class="group">
                      <label for="email" class="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                        <svg class="w-4 h-4 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                        </svg>
                        Correo Electrónico *
                      </label>
                      <div class="relative">
                        <input
                          v-model="userForm.email"
                          type="email"
                          id="email"
                          required
                          class="w-full px-4 py-3 border-2 border-gray-200 rounded-xl shadow-sm transition-all duration-200 focus:border-purple-500 focus:ring-4 focus:ring-purple-100 focus:outline-none hover:border-gray-300 bg-gray-50 focus:bg-white"
                          placeholder="correo@ejemplo.com"
                        />
                      </div>
                    </div>

                    <!-- Password -->
                    <div v-if="!isEditing" class="group">
                      <label for="password" class="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                        <svg class="w-4 h-4 mr-2 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Contraseña *
                      </label>
                      <div class="relative">
                        <input
                          v-model="userForm.password"
                          type="password"
                          id="password"
                          required
                          class="w-full px-4 py-3 border-2 border-gray-200 rounded-xl shadow-sm transition-all duration-200 focus:border-pink-500 focus:ring-4 focus:ring-pink-100 focus:outline-none hover:border-gray-300 bg-gray-50 focus:bg-white"
                          placeholder="Mínimo 8 caracteres"
                        />
                      </div>
                    </div>


                  </div>
                </div>
              </div>
            </div>
            <div class="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 sm:px-8 sm:flex sm:flex-row-reverse sm:gap-3">
              <button
                type="submit"
                :disabled="usersStore.isLoading"
                class="w-full inline-flex items-center justify-center rounded-xl border border-transparent shadow-lg px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-base font-semibold text-white hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-200 transform hover:scale-105 transition-all duration-200 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                <svg v-if="!usersStore.isLoading" class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                <svg v-else class="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ usersStore.isLoading ? 'Guardando...' : (isEditing ? 'Actualizar Usuario' : 'Crear Usuario') }}
              </button>
              <button
                type="button"
                @click="closeModal"
                class="mt-3 w-full inline-flex items-center justify-center rounded-xl border-2 border-gray-300 shadow-sm px-6 py-3 bg-white text-base font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-4 focus:ring-gray-200 transform hover:scale-105 transition-all duration-200 sm:mt-0 sm:w-auto sm:text-sm"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Block User Modal -->
    <div v-if="showBlockModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
        <div class="fixed inset-0 bg-gray-900 bg-opacity-60" @click="showBlockModal = false"></div>
        <div class="relative bg-white rounded-xl shadow-xl w-full max-w-md z-10">
          <div class="bg-gradient-to-r from-orange-500 to-red-600 px-6 py-4 rounded-t-xl">
            <h3 class="text-lg font-bold text-white">Bloquear acceso al app</h3>
            <p class="text-orange-100 text-sm mt-0.5">{{ userToBlock?.name }} no podrá iniciar sesión hasta ser desbloqueado</p>
          </div>
          <div class="px-6 py-5 space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1.5">Duración del bloqueo</label>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="opt in blockDurationOptions"
                  :key="opt.value"
                  @click="blockDuration = opt.value"
                  type="button"
                  class="px-3 py-1.5 text-sm rounded-lg border-2 transition-all"
                  :class="blockDuration === opt.value ? 'border-red-500 bg-red-50 text-red-700 font-medium' : 'border-gray-200 text-gray-600 hover:border-gray-300'"
                >
                  {{ opt.label }}
                </button>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1.5">Motivo del bloqueo (opcional)</label>
              <textarea
                v-model="blockReason"
                rows="3"
                placeholder="Ej: Comportamiento inapropiado, acoso a otros usuarios..."
                class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
              ></textarea>
            </div>
          </div>
          <div class="bg-gray-50 px-6 py-3 flex flex-row-reverse gap-2 rounded-b-xl">
            <button @click="executeBlock" :disabled="blockLoading" class="btn btn-danger">{{ blockLoading ? 'Bloqueando...' : 'Confirmar bloqueo' }}</button>
            <button @click="showBlockModal = false" class="btn btn-secondary">Cancelar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="showDeleteModal = false"></div>
        
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 class="text-lg leading-6 font-medium text-gray-900">Eliminar Usuario</h3>
                <div class="mt-2">
                  <p class="text-sm text-gray-500">
                    ¿Estás seguro de que quieres eliminar a <strong>{{ userToDelete?.name }}</strong>? Esta acción no se puede deshacer.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              @click="deleteUser"
              :disabled="usersStore.isLoading"
              class="btn btn-danger w-full sm:w-auto sm:ml-3"
            >
              {{ usersStore.isLoading ? 'Eliminando...' : 'Eliminar' }}
            </button>
            <button
              @click="showDeleteModal = false"
              class="btn btn-secondary w-full sm:w-auto mt-3 sm:mt-0"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- Unblock Confirm Modal -->
  <div v-if="showUnblockModal" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
      <div class="fixed inset-0 bg-gray-900 bg-opacity-60" @click="showUnblockModal = false"></div>
      <div class="relative bg-white rounded-xl shadow-xl w-full max-w-md z-10">
        <div class="px-6 pt-6 pb-4">
          <div class="flex items-start gap-4">
            <div class="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-green-100">
              <svg class="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-medium text-gray-900">Desbloquear usuario</h3>
              <p class="text-sm text-gray-500 mt-1">¿Confirmas que deseas desbloquear a <strong>{{ userToUnblock?.name }}</strong>? El usuario podrá volver a iniciar sesión inmediatamente.</p>
            </div>
          </div>
        </div>
        <div class="bg-gray-50 px-6 py-3 flex flex-row-reverse gap-2 rounded-b-xl">
          <button @click="unblockUser" :disabled="blockLoading" class="btn btn-primary">{{ blockLoading ? 'Desbloqueando...' : 'Confirmar desbloqueo' }}</button>
          <button @click="showUnblockModal = false" class="btn btn-secondary">Cancelar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useUsersStore } from '@/stores/users'
import { useAuthStore } from '@/stores/auth'
import { ActivityLogger } from '@/utils/activityLogger'
import { useToast } from 'vue-toastification'
import apiService from '@/services/api'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'

export default {
  name: 'Users',
  components: {
    DataTable,
    Column,
    InputText,
    IconField,
    InputIcon
  },
  setup() {
    const usersStore = useUsersStore()
    const authStore = useAuthStore()
    const router = useRouter()
    const toast = useToast()
    
    const showModal = ref(false)
    const showDeleteModal = ref(false)
    const showBlockModal = ref(false)
    const showUnblockModal = ref(false)
    const isEditing = ref(false)
    const userToDelete = ref(null)
    const userToBlock = ref(null)
    const userToUnblock = ref(null)
    const editingUserId = ref(null)
    const blockLoading = ref(false)
    const blockReason = ref('')
    const blockDuration = ref('indefinite')

    const blockDurationOptions = [
      { label: '1 día', value: '1d' },
      { label: '3 días', value: '3d' },
      { label: '7 días', value: '7d' },
      { label: '30 días', value: '30d' },
      { label: 'Indefinido', value: 'indefinite' }
    ]

    const getBlockedUntil = () => {
      if (blockDuration.value === 'indefinite') return undefined
      const days = parseInt(blockDuration.value)
      const date = new Date()
      date.setDate(date.getDate() + days)
      return date.toISOString()
    }

    const openBlockModal = (user) => {
      userToBlock.value = user
      blockReason.value = ''
      blockDuration.value = 'indefinite'
      showBlockModal.value = true
    }

    const executeBlock = async () => {
      blockLoading.value = true
      try {
        await apiService.api.patch(`/admin/users/${userToBlock.value.id}/block`, {
          action: 'block',
          reason: blockReason.value || undefined,
          blockedUntil: getBlockedUntil()
        })
        const u = usersStore.users.find(u => u.id === userToBlock.value.id)
        if (u) { u.isBlocked = true; u.blockedReason = blockReason.value || null }
        toast.success('Usuario bloqueado')
        showBlockModal.value = false
      } catch {
        toast.error('Error al bloquear al usuario')
      } finally {
        blockLoading.value = false
      }
    }

    const unblockUser = async () => {
      blockLoading.value = true
      try {
        await apiService.api.patch(`/admin/users/${userToUnblock.value.id}/block`, { action: 'unblock' })
        const u = usersStore.users.find(u => u.id === userToUnblock.value.id)
        if (u) { u.isBlocked = false; u.blockedReason = null; u.blockedUntil = null }
        toast.success('Usuario desbloqueado')
        showUnblockModal.value = false
      } catch {
        toast.error('Error al desbloquear al usuario')
      } finally {
        blockLoading.value = false
      }
    }
    const loading = ref(false)
    
    // Filters
    const globalFilter = ref('')
    const planFilter = ref('')
    
    const filters = ref({
      global: { value: null, matchMode: 'contains' }
    })
    
    const userForm = reactive({
      name: '',
      email: '',
      password: '',
      role: 'user'
    })

    // Watch for globalFilter changes
    watch(globalFilter, (newValue) => {
      filters.value.global.value = newValue
    })

    // Computed properties
    const filteredUsers = computed(() => {
      let users = usersStore.users || []
      
      users = users.filter(user => user.role === 'user')
      
      if (globalFilter.value) {
        const searchTerm = globalFilter.value.toLowerCase()
        users = users.filter(user => 
          user.name?.toLowerCase().includes(searchTerm) ||
          user.email?.toLowerCase().includes(searchTerm)
        )
      }

      if (planFilter.value === 'premium') {
        users = users.filter(user => user.plan?.type === 'premium')
      } else if (planFilter.value === 'free') {
        users = users.filter(user => !user.plan || user.plan.type === 'free')
      }
      
      return users
    })

    // Methods
    const loadUsers = async () => {
      loading.value = true
      try {
        await usersStore.fetchUsers({ limit: 10000 })
      } catch (error) {
        console.error('Error loading users:', error)
      } finally {
        loading.value = false
      }
    }

    const openCreateModal = () => {
      isEditing.value = false
      editingUserId.value = null
      resetForm()
      showModal.value = true
    }

    const openEditModal = (user) => {
      isEditing.value = true
      editingUserId.value = user.id
      userForm.name = user.name
      userForm.email = user.email
      userForm.role = user.role
      userForm.password = ''
      showModal.value = true
    }

    const editUser = (user) => {
      openEditModal(user)
    }

    const closeModal = () => {
      showModal.value = false
      resetForm()
    }

    const resetForm = () => {
      userForm.name = ''
      userForm.email = ''
      userForm.password = ''
      userForm.role = 'user'
    }

    const saveUser = async () => {
      let result
      
      if (isEditing.value) {
        const updateData = {
          name: userForm.name,
          email: userForm.email
        }
        result = await usersStore.updateUser(editingUserId.value, updateData)
        
        if (result) {
          // Log activity
          await ActivityLogger.logUpdate(
            authStore.user?.id || 0,
            'user',
            editingUserId.value,
            userForm.name
          )
        }
      } else {
        result = await usersStore.createUser({
          name: userForm.name,
          email: userForm.email,
          password: userForm.password,
          role: userForm.role
        })
        
        if (result) {
          // Log activity
          await ActivityLogger.logCreate(
            authStore.user?.id || 0,
            'user',
            result.id || 0,
            userForm.name
          )
        }
      }
      
      if (result) {
        closeModal()
      }
    }

    const confirmDelete = (user) => {
      userToDelete.value = user
      showDeleteModal.value = true
    }

    const deleteUser = async () => {
      const userToDeleteData = userToDelete.value
      const result = await usersStore.deleteUser(userToDeleteData.id)
      
      if (result) {
        // Log activity
        await ActivityLogger.logDelete(
          authStore.user?.id || 0,
          'user',
          userToDeleteData.id,
          userToDeleteData.name
        )
        
        showDeleteModal.value = false
        userToDelete.value = null
      }
    }

    const toggleUserStatus = async (user) => {
      await usersStore.toggleUserStatus(user.id)
    }

    const formatDate = (dateString) => {
      const date = new Date(dateString)
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }

    onMounted(() => {
      loadUsers()
    })

    return {
      usersStore,
      router,
      showModal,
      showDeleteModal,
      showBlockModal,
      showUnblockModal,
      userToUnblock,
      blockLoading,
      blockReason,
      blockDuration,
      blockDurationOptions,
      userToBlock,
      isEditing,
      userToDelete,
      filters,
      userForm,
      globalFilter,
      planFilter,
      loading,
      filteredUsers,
      loadUsers,
      openCreateModal,
      openEditModal,
      editUser,
      closeModal,
      resetForm,
      saveUser,
      confirmDelete,
      deleteUser,
      openBlockModal,
      executeBlock,
      unblockUser,
      toggleUserStatus,
      formatDate
    }
  }
}
</script>